import { createContext, useContext, useState, ReactNode, useMemo, useCallback } from "react";

interface SessionTimerContextValue {
  elapsedSeconds: number;
  isActive: boolean;
  setElapsed: (s: number) => void;
  reset: () => void;
}

const SessionTimerContext = createContext<SessionTimerContextValue | undefined>(undefined);

export function SessionTimerProvider({ children }: { children: ReactNode }) {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isActive, setIsActive] = useState(false);

  const setElapsed = useCallback((s: number) => {
    setElapsedSeconds(s);
    setIsActive(s > 0);
  }, []);

  const reset = useCallback(() => {
    setElapsedSeconds(0);
    setIsActive(false);
  }, []);

  const value = useMemo(
    () => ({ elapsedSeconds, isActive, setElapsed, reset }),
    [elapsedSeconds, isActive, setElapsed, reset]
  );

  return <SessionTimerContext.Provider value={value}>{children}</SessionTimerContext.Provider>;
}

export function useSessionTimer() {
  const ctx = useContext(SessionTimerContext);
  if (!ctx) throw new Error("useSessionTimer must be used within SessionTimerProvider");
  return ctx;
}
